import IconButton from '@mui/material/IconButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PropTypes from 'prop-types';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';

import CopyButton from '../../ui/copy-button.jsx';
import ScrollingTitle from '@explorer/components/ui/scrolling-title';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import formatMosaicDate from '@explorer/util/format-mosaic-date';
import {COMPARE_TYPES} from '@explorer/constants';
import {
  ListItem,
  RequestAccessButton,
  THUMBNAIL_DIMENSIONS,
} from '../../items-panel/item-components.jsx';
import {trackEvent} from '@explorer/analytics';

const MosaicListItem = ({mosaic, isComparing, ...props}) => {
  const {t} = useTranslation();

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.MOSAIC,
      item: mosaic,
      canDrag: isComparing && mosaic.hasAccess,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const onRequestAccessClick = mosaic => event => {
    event.stopPropagation();
    props.onRequestAccess(mosaic);
  };

  const handleClickBasemapsLink = () => {
    trackEvent('Redirected to Basemaps Viewer', {
      mosaicName: mosaic.name,
    });
  };

  const handleMosaicNameCopy = (__, e) => {
    e.stopPropagation();
    trackEvent('Basemap Item: Copied basemap name', {
      mosaicId: mosaic.id,
      mosaicName: mosaic.name,
      seriesId: mosaic.seriesId,
      seriesName: mosaic.seriesName,
    });
  };

  return (
    <ListItem
      key={props.key}
      divider
      ref={dragRef}
      isDragging={isDragging}
      isComparing={isComparing}
      button={!isComparing && mosaic.hasAccess}
      selected={props.selected}
      onClick={() => !isComparing && props.onClick()}
      disabled={!mosaic.hasAccess}
      data-qe="mosaic-list-item"
    >
      <ThumbnailCard
        height={THUMBNAIL_DIMENSIONS}
        width={THUMBNAIL_DIMENSIONS}
        marginRight={2}
        selected={props.selected}
        thumbnail={<ThumbnailImage src={mosaic._links.thumb} />}
      >
        {!mosaic.hasAccess && (
          <RequestAccessButton
            data-qe="mosaic-item-access-button"
            onClick={onRequestAccessClick(mosaic)}
          >
            {t('common:purchaseAccess', 'Purchase Access')}
          </RequestAccessButton>
        )}
      </ThumbnailCard>
      <ListItemText disableTypography>
        <Typography variant="subtitle1" data-qe="mosaic-date-label">
          {t(...formatMosaicDate(mosaic))}
        </Typography>
        <ScrollingTitle
          align="left"
          variant="body2"
          data-qe="mosaic-description"
        >
          {mosaic.name}
        </ScrollingTitle>
        <CopyButton
          onCopy={handleMosaicNameCopy}
          size="small"
          textToCopy={mosaic.name}
          title={t('sidebarsBasemap.copyTooltipTypeBasemapName')}
          data-qe="copy-button"
        />
      </ListItemText>

      <ListItemSecondaryAction>
        <Tooltip title={t('sidebarsBasemap.tooltipViewBasemap')}>
          <IconButton
            onClick={handleClickBasemapsLink}
            href={`https://www.planet.com/basemaps/#/mosaic/${mosaic.name}`}
            target="_blank"
            rel="noopener noreferrer"
            edge="end"
            data-qe="basemap-viewer-button"
          >
            <OpenInNewOutlinedIcon />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
};

MosaicListItem.propTypes = {
  isComparing: PropTypes.bool,
  mosaic: PropTypes.object.isRequired,
  onRequestAccess: PropTypes.func,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  key: PropTypes.string,
};

export default MosaicListItem;
